import React, { useState } from 'react';
import { Download, Layers, CheckCircle2, Copy, Check, Type, Palette, Image as ImageIcon, FileBox, ArrowUpRight, Loader2 } from 'lucide-react';
import { exportFigmaBundle, exportAffinityBundle, exportTokensJSON, exportLogoSetSVG, exportPNGSheet, exportPrintPack, type FrameRecord } from '../../../lib/exporters';
const C_YELLOW = '#F0B92B';
const C_AMBER = '#D9711F';
const C_BURGUNDY = '#8B2E3F';
const C_VIOLET = '#5B3A82';
const C_GREEN = '#2D7A4F';
const C_BLUE = '#2E5C8A';
const FN_ICON = 'https://cdn.magicpatterns.com/uploads/6S42RJUThA4LVcV5MgRYxz/filmnotes-fn-full-icon_1024.png';
type Cat = 'LAB' | 'TIP' | 'FEATURE' | 'COMMUNITY' | 'GEAR' | 'SYSTEM';
const catColor: Record<Cat, string> = {
  LAB: C_BLUE,
  TIP: C_AMBER,
  FEATURE: C_BURGUNDY,
  COMMUNITY: C_VIOLET,
  GEAR: C_GREEN,
  SYSTEM: '#2B2B2B'
};
interface Frame {
  id: string;
  name: string;
  dims: string;
  ratio: string;
  cat: Cat;
  page: string;
}
// Catalogue of every frame in the library
const frames: Frame[] = [
// 02 Feed
{
  id: 'CAT_TIP / FMT_REEL_COVER',
  name: 'Sunny 16 cover',
  dims: '1080×1920',
  ratio: '9/16',
  cat: 'TIP',
  page: '02_FEED'
}, {
  id: 'CAT_FEATURE / FMT_REEL_COVER',
  name: 'Track every roll',
  dims: '1080×1920',
  ratio: '9/16',
  cat: 'FEATURE',
  page: '02_FEED'
}, {
  id: 'CAT_COMMUNITY / FMT_REEL_COVER',
  name: 'Tokyo at night',
  dims: '1080×1920',
  ratio: '9/16',
  cat: 'COMMUNITY',
  page: '02_FEED'
}, {
  id: 'CAT_GEAR / FMT_REEL_COVER',
  name: 'FM2 field notes',
  dims: '1080×1920',
  ratio: '9/16',
  cat: 'GEAR',
  page: '02_FEED'
}, {
  id: 'CAT_FEATURE / FMT_CAROUSEL · App',
  name: 'App walkthrough · 6 slides',
  dims: '1080×1350',
  ratio: '4/5',
  cat: 'FEATURE',
  page: '02_FEED'
}, {
  id: 'CAT_GEAR / FMT_CAROUSEL · Gear',
  name: 'Best 35mm · 6 slides',
  dims: '1080×1350',
  ratio: '4/5',
  cat: 'GEAR',
  page: '02_FEED'
}, {
  id: 'CAT_LAB / FMT_CAROUSEL · Lab',
  name: 'Photo Works Belfast · 5 slides',
  dims: '1080×1350',
  ratio: '4/5',
  cat: 'LAB',
  page: '02_FEED'
}, {
  id: 'CAT_TIP / FMT_CAROUSEL · Tip',
  name: 'Sharper scans · 5 slides',
  dims: '1080×1350',
  ratio: '4/5',
  cat: 'TIP',
  page: '02_FEED'
}, {
  id: 'CAT_COMMUNITY / FMT_SINGLE · Portrait',
  name: 'Morning fog scan',
  dims: '1080×1080',
  ratio: '1/1',
  cat: 'COMMUNITY',
  page: '02_FEED'
}, {
  id: 'CAT_COMMUNITY / FMT_SINGLE · Landscape',
  name: 'Coastline split',
  dims: '1080×1080',
  ratio: '1/1',
  cat: 'COMMUNITY',
  page: '02_FEED'
}, {
  id: 'CAT_TIP / FMT_SINGLE · Pull-quote',
  name: 'Meter for shadows',
  dims: '1080×1080',
  ratio: '1/1',
  cat: 'TIP',
  page: '02_FEED'
}, {
  id: 'CAT_FEATURE / FMT_SINGLE · News',
  name: 'News of May',
  dims: '1080×1080',
  ratio: '1/1',
  cat: 'FEATURE',
  page: '02_FEED'
}, {
  id: 'CAT_GEAR / FMT_SINGLE · Stock',
  name: 'Stock of the week',
  dims: '1080×1080',
  ratio: '1/1',
  cat: 'GEAR',
  page: '02_FEED'
}, {
  id: 'CAT_TIP / FMT_SINGLE_4_5',
  name: 'Pro Tip · 4:5',
  dims: '1080×1350',
  ratio: '4/5',
  cat: 'TIP',
  page: '02_FEED'
}, {
  id: 'CAT_GEAR / FMT_SINGLE_4_5',
  name: 'Stock highlight · 4:5',
  dims: '1080×1350',
  ratio: '4/5',
  cat: 'GEAR',
  page: '02_FEED'
}, {
  id: 'CAT_COMMUNITY / FMT_SINGLE_4_5',
  name: 'Hero · 4:5',
  dims: '1080×1350',
  ratio: '4/5',
  cat: 'COMMUNITY',
  page: '02_FEED'
},
// 03 Stories
{
  id: 'FMT_STORY · Reshare + Poll',
  name: 'Reshare + poll',
  dims: '1080×1920',
  ratio: '9/16',
  cat: 'FEATURE',
  page: '03_STORIES'
}, {
  id: 'FMT_STORY · Question',
  name: 'Question prompt',
  dims: '1080×1920',
  ratio: '9/16',
  cat: 'COMMUNITY',
  page: '03_STORIES'
}, {
  id: 'FMT_STORY · BTS Photo',
  name: 'BTS photo + voice',
  dims: '1080×1920',
  ratio: '9/16',
  cat: 'COMMUNITY',
  page: '03_STORIES'
}, {
  id: 'FMT_STORY · UGC Thank-you',
  name: 'UGC thank-you',
  dims: '1080×1920',
  ratio: '9/16',
  cat: 'COMMUNITY',
  page: '03_STORIES'
}, {
  id: 'FMT_STORY · Countdown',
  name: 'Countdown',
  dims: '1080×1920',
  ratio: '9/16',
  cat: 'FEATURE',
  page: '03_STORIES'
}, {
  id: 'FMT_STORY · Link CTA',
  name: 'Link sticker CTA',
  dims: '1080×1920',
  ratio: '9/16',
  cat: 'FEATURE',
  page: '03_STORIES'
}, {
  id: 'STORY_SEQ_01-03',
  name: '3-frame sequence',
  dims: '1080×1920 ×3',
  ratio: '9/16',
  cat: 'SYSTEM',
  page: '03_STORIES'
},
// 04 Print
{
  id: 'FMT_PRINT / LAB_CARD · Square',
  name: 'Square lab card',
  dims: '90×90mm',
  ratio: '1/1',
  cat: 'LAB',
  page: '04_PRINT'
}, {
  id: 'FMT_PRINT / LAB_CARD · A6',
  name: 'A6 lab card · F&B',
  dims: '105×148mm',
  ratio: '105/148',
  cat: 'LAB',
  page: '04_PRINT'
}, {
  id: 'FMT_PRINT / LAB_CARD · 4:5',
  name: '4:5 studio card',
  dims: '100×125mm',
  ratio: '4/5',
  cat: 'LAB',
  page: '04_PRINT'
}, {
  id: 'FMT_PRINT / LAB_CARD · A5',
  name: 'A5 lab flyer',
  dims: '148×210mm',
  ratio: '148/210',
  cat: 'LAB',
  page: '04_PRINT'
}, {
  id: 'FMT_PRINT / STICKER · Circular',
  name: 'Circular red logo',
  dims: '60mm Ø',
  ratio: '1/1',
  cat: 'SYSTEM',
  page: '04_PRINT'
}, {
  id: 'FMT_PRINT / STICKER · Square Mantra',
  name: 'Square mantra',
  dims: '50×50mm',
  ratio: '1/1',
  cat: 'SYSTEM',
  page: '04_PRINT'
}, {
  id: 'FMT_PRINT / STICKER · Wordmark',
  name: 'Wordmark die-cut',
  dims: '80×30mm',
  ratio: '80/30',
  cat: 'SYSTEM',
  page: '04_PRINT'
}, {
  id: 'FMT_PRINT / STICKER · Logged Pill',
  name: 'Logged-in badge',
  dims: '100×30mm',
  ratio: '100/30',
  cat: 'SYSTEM',
  page: '04_PRINT'
}, {
  id: 'FMT_PRINT / STICKER · Clear Frame',
  name: 'Clear-center frame',
  dims: '70×70mm',
  ratio: '1/1',
  cat: 'SYSTEM',
  page: '04_PRINT'
}, {
  id: 'FMT_PRINT / STICKER · Polaroid',
  name: 'Polaroid clear vinyl',
  dims: '80×95mm',
  ratio: '80/95',
  cat: 'SYSTEM',
  page: '04_PRINT'
}, {
  id: 'FMT_PRINT / POSTCARD · F&B',
  name: 'Postcard · landscape',
  dims: '148×105mm',
  ratio: '148/105',
  cat: 'COMMUNITY',
  page: '04_PRINT'
},
// 05 Ads
{
  id: 'FMT_AD · Reel',
  name: 'Reel ad · paid',
  dims: '1080×1920',
  ratio: '9/16',
  cat: 'FEATURE',
  page: '05_ADS'
}, {
  id: 'FMT_AD · Carousel',
  name: 'Carousel ad · paid',
  dims: '1080×1350',
  ratio: '4/5',
  cat: 'FEATURE',
  page: '05_ADS'
}];
const pageGroups: {
  id: string;
  title: string;
  description: string;
}[] = [{
  id: '02_FEED',
  title: '02 · Feed',
  description: 'Reel covers, carousels, singles · 1:1 and 4:5'
}, {
  id: '03_STORIES',
  title: '03 · Stories',
  description: 'Day-on/day-off rhythm · 9:16 with safe zones'
}, {
  id: '04_PRINT',
  title: '04 · Print & Offline',
  description: 'Lab cards in 4 sizes, stickers, postcard'
}, {
  id: '05_ADS',
  title: '05 · Ad Variants',
  description: 'Paid extensions'
}];
// Renders a tiny visual stand-in for the frame (proportional rectangle + category accent)
function FrameThumb({
  frame
}: {
  frame: Frame;
}) {
  const color = catColor[frame.cat];
  return <div className="relative w-full bg-film-cream border border-film-border overflow-hidden" style={{
    aspectRatio: frame.ratio
  }} data-id="element-1023">
      {/* Category accent bar */}
      <div className="absolute left-0 top-0 bottom-0" style={{
      width: '4px',
      backgroundColor: color
    }} data-id="element-1024" />
      {/* Tiny representation lines (simulate content blocks) */}
      <div className="absolute inset-3 left-4 flex flex-col gap-1.5 justify-end" data-id="element-1025">
        <div className="h-1.5 w-8 bg-film-black/15" data-id="element-1026" />
        <div className="h-2.5 w-3/4 bg-film-black/40" data-id="element-1027" />
        <div className="h-2.5 w-1/2 bg-film-black/40" data-id="element-1028" />
        <div className="h-0.5 w-4 bg-film-black/10 mt-1" data-id="element-1029" />
        <div className="h-1 w-1/3 bg-film-black/10" data-id="element-1030" />
      </div>
      {/* Cat tag corner */}
      <div className="absolute top-1.5 right-1.5 font-mono text-[7px] uppercase tracking-wider text-white px-1 py-0.5" style={{
      backgroundColor: color
    }} data-id="element-1031">
        {frame.cat}
      </div>
    </div>;
}
function CopyBlock({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  const [copied, setCopied] = useState(false);
  return <button onClick={() => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }} className="w-full text-left bg-film-cream border border-film-border px-3 py-2 hover:border-film-black/30 transition-colors flex items-center justify-between group" data-id="element-1032">
      <div className="min-w-0" data-id="element-1033">
        <p className="font-mono text-[8px] uppercase tracking-wider text-film-gray mb-0.5" data-id="element-1034">
          {label}
        </p>
        <p className="font-mono text-[10px] text-film-black truncate" data-id="element-1035">
          {value}
        </p>
      </div>
      {copied ? <Check className="w-3 h-3 text-film-red shrink-0 ml-2" data-id="element-1036" /> : <Copy className="w-3 h-3 text-film-gray group-hover:text-film-black shrink-0 ml-2" data-id="element-1037" />}
    </button>;
}
// Hook to manage per-button async state
type ExportState = 'idle' | 'working' | 'done';
function useExporter() {
  const [states, setStates] = useState<Record<string, ExportState>>({});
  const run = async (key: string, fn: () => Promise<void> | void) => {
    setStates(s => ({
      ...s,
      [key]: 'working'
    }));
    try {
      await fn();
      setStates(s => ({
        ...s,
        [key]: 'done'
      }));
      setTimeout(() => setStates(s => ({
        ...s,
        [key]: 'idle'
      })), 1800);
    } catch (err) {
      console.error('Export failed', err);
      setStates(s => ({
        ...s,
        [key]: 'idle'
      }));
    }
  };
  return {
    states,
    run
  };
}
export function MasterExportPage() {
  const totalFrames = frames.length;
  const totalFormats = new Set(frames.map(f => f.ratio)).size;
  const totalCats = new Set(frames.map(f => f.cat)).size;
  const {
    states,
    run
  } = useExporter();
  const figmaState = states['figma'] || 'idle';
  const affinityState = states['affinity'] || 'idle';
  return <div className="max-w-6xl mx-auto py-12 px-8 space-y-16" data-id="element-1038">
      {/* Header */}
      <div data-id="element-1039">
        <p className="font-mono text-xs text-film-gray mb-3" data-id="element-1040">
          06_MASTER_EXPORT
        </p>
        <h1 className="font-display font-bold text-4xl text-film-black mb-2" data-id="element-1041">
          Master · All Templates
        </h1>
        <p className="text-film-gray max-w-2xl" data-id="element-1042">
          Every frame in the library, one canvas. Use this page as a quick
          inventory before exporting to Figma or Affinity.
        </p>
      </div>

      {/* Stats + Export controls */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1043">
        {/* Stats */}
        <div className="bg-white border border-film-border shadow-card p-6 relative" data-id="element-1044">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-film-red" data-id="element-1045" />
          <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-3" data-id="element-1046">
            Inventory
          </p>
          <div className="grid grid-cols-3 gap-2" data-id="element-1047">
            <div data-id="element-1048">
              <p className="font-display font-bold text-3xl text-film-black leading-none" data-id="element-1049">
                {totalFrames}
              </p>
              <p className="font-mono text-[9px] text-film-gray uppercase tracking-wider mt-1" data-id="element-1050">
                Frames
              </p>
            </div>
            <div data-id="element-1051">
              <p className="font-display font-bold text-3xl text-film-black leading-none" data-id="element-1052">
                {totalFormats}
              </p>
              <p className="font-mono text-[9px] text-film-gray uppercase tracking-wider mt-1" data-id="element-1053">
                Ratios
              </p>
            </div>
            <div data-id="element-1054">
              <p className="font-display font-bold text-3xl text-film-black leading-none" data-id="element-1055">
                {totalCats}
              </p>
              <p className="font-mono text-[9px] text-film-gray uppercase tracking-wider mt-1" data-id="element-1056">
                Cats
              </p>
            </div>
          </div>
        </div>

        {/* Figma export */}
        <div className="bg-film-black text-white shadow-card p-6 relative overflow-hidden" data-id="element-1057">
          <div className="flex items-center justify-between mb-2" data-id="element-1058">
            <p className="font-mono text-[10px] uppercase tracking-wider text-white/50" data-id="element-1059">
              Export · Figma
            </p>
            <FileBox className="w-4 h-4 text-white/40" data-id="element-1060" />
          </div>
          <p className="font-display font-bold text-xl leading-tight mb-3" data-id="element-1061">
            Tokens + frames + logos.
          </p>
          <p className="text-xs text-white/60 leading-snug mb-4" data-id="element-1062">
            ZIP with tokens.json (Tokens Studio plugin), SVG logos, color
            swatches, and one placeholder SVG per frame at the correct ratio.
          </p>
          <button onClick={() => run('figma', () => exportFigmaBundle(frames as unknown as FrameRecord[]))} disabled={figmaState === 'working'} className="w-full bg-white text-film-black font-mono text-[10px] uppercase tracking-wider px-3 py-2 flex items-center justify-between hover:bg-film-cream transition-colors disabled:opacity-60" data-id="element-1063">
            <span data-id="element-1064">
              {figmaState === 'working' ? 'Bundling…' : figmaState === 'done' ? 'Downloaded ✓' : 'Download Figma bundle (.zip)'}
            </span>
            {figmaState === 'working' ? <Loader2 className="w-3 h-3 animate-spin" data-id="element-1065" /> : figmaState === 'done' ? <Check className="w-3 h-3" data-id="element-1066" /> : <ArrowUpRight className="w-3 h-3" data-id="element-1067" />}
          </button>
        </div>

        {/* Affinity export */}
        <div className="text-white shadow-card p-6 relative overflow-hidden" style={{
        backgroundColor: C_BLUE
      }} data-id="element-1068">
          <div className="flex items-center justify-between mb-2" data-id="element-1069">
            <p className="font-mono text-[10px] uppercase tracking-wider text-white/60" data-id="element-1070">
              Export · Affinity
            </p>
            <Layers className="w-4 h-4 text-white/60" data-id="element-1071" />
          </div>
          <p className="font-display font-bold text-xl leading-tight mb-3" data-id="element-1072">
            CMYK-ready bundle.
          </p>
          <p className="text-xs text-white/70 leading-snug mb-4" data-id="element-1073">
            ZIP with manifest, CMYK breakdowns, swatch SVGs, logos, and print
            frame placeholders for Affinity Publisher 2.
          </p>
          <button onClick={() => run('affinity', () => exportAffinityBundle(frames as unknown as FrameRecord[]))} disabled={affinityState === 'working'} className="w-full bg-white font-mono text-[10px] uppercase tracking-wider px-3 py-2 flex items-center justify-between hover:bg-film-cream transition-colors disabled:opacity-60" style={{
          color: C_BLUE
        }} data-id="element-1074">
            <span data-id="element-1075">
              {affinityState === 'working' ? 'Bundling…' : affinityState === 'done' ? 'Downloaded ✓' : 'Download Affinity bundle (.zip)'}
            </span>
            {affinityState === 'working' ? <Loader2 className="w-3 h-3 animate-spin" data-id="element-1076" /> : affinityState === 'done' ? <Check className="w-3 h-3" data-id="element-1077" /> : <ArrowUpRight className="w-3 h-3" data-id="element-1078" />}
          </button>
        </div>
      </div>

      {/* Quick exports row */}
      <div className="bg-white border border-film-border shadow-card" data-id="element-1079">
        <div className="px-5 py-3 border-b border-film-border bg-film-cream flex items-center justify-between" data-id="element-1080">
          <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-1081">
            Quick exports
          </p>
          <p className="font-mono text-[10px] text-film-gray" data-id="element-1082">
            Static · no-Figma needed
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-film-border" data-id="element-1083">
          {[{
          key: 'png',
          label: 'Contact sheet · all frames',
          meta: 'SVG · scalable poster',
          accent: '#C93B3B',
          fn: () => exportPNGSheet(frames as unknown as FrameRecord[])
        }, {
          key: 'pdf',
          label: 'Print pack',
          meta: 'ZIP · CMYK + specs',
          accent: C_BURGUNDY,
          fn: () => exportPrintPack(frames as unknown as FrameRecord[])
        }, {
          key: 'svg',
          label: 'SVG · logo set',
          meta: '5 variants · vector',
          accent: C_BLUE,
          fn: () => exportLogoSetSVG()
        }, {
          key: 'json',
          label: 'JSON · tokens',
          meta: 'Tokens Studio format',
          accent: C_GREEN,
          fn: () => exportTokensJSON()
        }].map(e => {
          const st = states[e.key] || 'idle';
          return <button key={e.key} onClick={() => run(e.key, e.fn)} disabled={st === 'working'} className="p-5 text-left hover:bg-film-cream/40 transition-colors relative group disabled:opacity-60" data-id="element-1084">
                <div className="absolute left-0 top-0 bottom-0 w-1" style={{
              backgroundColor: e.accent
            }} data-id="element-1085" />
                {st === 'working' ? <Loader2 className="w-4 h-4 text-film-gray mb-3 animate-spin" data-id="element-1086" /> : st === 'done' ? <Check className="w-4 h-4 mb-3" style={{
              color: e.accent
            }} data-id="element-1087" /> : <Download className="w-4 h-4 text-film-gray mb-3" data-id="element-1088" />}
                <p className="font-display font-bold text-sm text-film-black leading-tight mb-1" data-id="element-1089">
                  {st === 'done' ? 'Downloaded ✓' : e.label}
                </p>
                <p className="font-mono text-[9px] text-film-gray uppercase tracking-wider" data-id="element-1090">
                  {e.meta}
                </p>
              </button>;
        })}
        </div>
      </div>

      {/* Pre-export checklist */}
      <section data-id="element-1091">
        <div className="mb-6" data-id="element-1092">
          <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-1" data-id="element-1093">
            § PRE_EXPORT
          </p>
          <h2 className="font-display font-bold text-2xl text-film-black" data-id="element-1094">
            Pre-export checklist
          </h2>
          <p className="text-sm text-film-gray mt-1 max-w-xl" data-id="element-1095">
            Send these alongside any export so the receiving tool can render
            everything correctly on first open.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1096">
          {/* Fonts */}
          <div className="bg-white border border-film-border p-5 shadow-card" data-id="element-1097">
            <div className="flex items-center gap-2 mb-3" data-id="element-1098">
              <Type className="w-4 h-4 text-film-black" data-id="element-1099" />
              <p className="font-display font-bold text-film-black" data-id="element-1100">Fonts</p>
            </div>
            <ul className="space-y-2 text-sm" data-id="element-1101">
              <li className="flex items-baseline gap-2" data-id="element-1102">
                <CheckCircle2 className="w-3 h-3 shrink-0 translate-y-[1px]" style={{
                color: C_GREEN
              }} data-id="element-1103" />
                <div data-id="element-1104">
                  <span className="font-display font-bold text-film-black" data-id="element-1105">
                    Futura PT
                  </span>{' '}
                  <span className="font-mono text-[10px] text-film-gray" data-id="element-1106">
                    Bold / Heavy
                  </span>
                </div>
              </li>
              <li className="flex items-baseline gap-2" data-id="element-1107">
                <CheckCircle2 className="w-3 h-3 shrink-0 translate-y-[1px]" style={{
                color: C_GREEN
              }} data-id="element-1108" />
                <div data-id="element-1109">
                  <span className="font-display font-bold text-film-black" data-id="element-1110">
                    Helvetica Neue
                  </span>{' '}
                  <span className="font-mono text-[10px] text-film-gray" data-id="element-1111">
                    Regular / Medium
                  </span>
                </div>
              </li>
              <li className="flex items-baseline gap-2" data-id="element-1112">
                <CheckCircle2 className="w-3 h-3 shrink-0 translate-y-[1px]" style={{
                color: C_GREEN
              }} data-id="element-1113" />
                <div data-id="element-1114">
                  <span className="font-display font-bold text-film-black" data-id="element-1115">
                    JetBrains Mono
                  </span>{' '}
                  <span className="font-mono text-[10px] text-film-gray" data-id="element-1116">
                    Regular · for CAT_/FMT_ labels
                  </span>
                </div>
              </li>
            </ul>
            <p className="font-mono text-[9px] text-film-gray mt-3 leading-snug" data-id="element-1117">
              Activate the fonts in your system before opening the .fig /
              .afpub.
            </p>
          </div>

          {/* Color tokens */}
          <div className="bg-white border border-film-border p-5 shadow-card" data-id="element-1118">
            <div className="flex items-center gap-2 mb-3" data-id="element-1119">
              <Palette className="w-4 h-4 text-film-black" data-id="element-1120" />
              <p className="font-display font-bold text-film-black" data-id="element-1121">
                Color tokens
              </p>
            </div>
            <div className="grid grid-cols-2 gap-1.5" data-id="element-1122">
              {[{
              name: 'fn-red',
              hex: '#C93B3B'
            }, {
              name: 'fn-orange',
              hex: '#FF8C42'
            }, {
              name: 'fn-black',
              hex: '#2B2B2B'
            }, {
              name: 'fn-cream',
              hex: '#F5F5F0'
            }, {
              name: 'kodak-yellow',
              hex: C_YELLOW
            }, {
              name: 'safelight-amber',
              hex: C_AMBER
            }, {
              name: 'darkroom-burgundy',
              hex: C_BURGUNDY
            }, {
              name: 'kodachrome-violet',
              hex: C_VIOLET
            }, {
              name: 'fuji-green',
              hex: C_GREEN
            }, {
              name: 'ektar-blue',
              hex: C_BLUE
            }].map(t => <div key={t.name} className="flex items-center gap-1.5 px-1.5 py-1 border border-film-border" data-id="element-1123">
                  <div className="w-3 h-3 shrink-0" style={{
                backgroundColor: t.hex
              }} data-id="element-1124" />
                  <span className="font-mono text-[8px] text-film-black truncate" data-id="element-1125">
                    {t.name}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Logo set */}
          <div className="bg-white border border-film-border p-5 shadow-card" data-id="element-1126">
            <div className="flex items-center gap-2 mb-3" data-id="element-1127">
              <ImageIcon className="w-4 h-4 text-film-black" data-id="element-1128" />
              <p className="font-display font-bold text-film-black" data-id="element-1129">Logo set</p>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-3" data-id="element-1130">
              {[{
              bg: '#F5F5F0',
              src: FN_ICON
            }, {
              bg: '#FFFFFF',
              src: FN_ICON
            }, {
              bg: '#2B2B2B',
              src: FN_ICON,
              invert: true
            }, {
              bg: '#C93B3B',
              src: FN_ICON,
              invert: true
            }].map((l, i) => <div key={i} className="aspect-square border border-film-border flex items-center justify-center" style={{
              backgroundColor: l.bg
            }} data-id="element-1131">
                  <img src={l.src} className={`w-1/2 ${l.invert ? 'brightness-0 invert' : ''}`} alt="" data-id="element-1132" />
                </div>)}
            </div>
            <p className="font-mono text-[9px] text-film-gray leading-snug" data-id="element-1133">
              SVG · 5 variants exported together. Use brand context when
              picking.
            </p>
          </div>

          {/* Image library */}
          <div className="bg-white border border-film-border p-5 shadow-card" data-id="element-1134">
            <div className="flex items-center gap-2 mb-3" data-id="element-1135">
              <ImageIcon className="w-4 h-4 text-film-black" data-id="element-1136" />
              <p className="font-display font-bold text-film-black" data-id="element-1137">
                Image library
              </p>
            </div>
            <ul className="space-y-1.5 text-[11px]" data-id="element-1138">
              {[{
              label: 'Community scans',
              count: '12 photos'
            }, {
              label: 'Lab interiors',
              count: '6 photos'
            }, {
              label: 'Gear shots',
              count: '8 photos'
            }, {
              label: 'BTS / process',
              count: '5 photos'
            }].map(row => <li key={row.label} className="flex justify-between border-b border-film-border/60 pb-1" data-id="element-1139">
                  <span className="text-film-black" data-id="element-1140">{row.label}</span>
                  <span className="font-mono text-film-gray text-[9px]" data-id="element-1141">
                    {row.count}
                  </span>
                </li>)}
            </ul>
            <p className="font-mono text-[9px] text-film-gray mt-3 leading-snug" data-id="element-1142">
              All shared with permission. Tagged in metadata with shooter +
              stock + camera.
            </p>
          </div>
        </div>
      </section>

      {/* The Master grid */}
      <section data-id="element-1143">
        <div className="mb-6" data-id="element-1144">
          <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-1" data-id="element-1145">
            § MASTER_GRID · {totalFrames} frames
          </p>
          <h2 className="font-display font-bold text-2xl text-film-black" data-id="element-1146">
            All frames, one canvas
          </h2>
          <p className="text-sm text-film-gray mt-1 max-w-xl" data-id="element-1147">
            Grouped by page. Each card shows the frame's category accent, ratio,
            and Figma-ready name.
          </p>
        </div>

        {/* Legend */}
        <div className="bg-white border border-film-border px-4 py-3 mb-6 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-1148">
          <p className="font-mono text-[9px] uppercase tracking-wider text-film-gray" data-id="element-1149">
            Legend
          </p>
          {(Object.keys(catColor) as Cat[]).map(c => <div key={c} className="flex items-center gap-1.5" data-id="element-1150">
              <div className="w-2 h-2" style={{
            backgroundColor: catColor[c]
          }} data-id="element-1151" />
              <span className="font-mono text-[10px] text-film-black" data-id="element-1152">
                CAT_{c}
              </span>
            </div>)}
        </div>

        {/* Groups */}
        <div className="space-y-10" data-id="element-1153">
          {pageGroups.map(group => {
          const groupFrames = frames.filter(f => f.page === group.id);
          return <div key={group.id} data-id="element-1154">
                <div className="flex items-baseline justify-between mb-4" data-id="element-1155">
                  <div data-id="element-1156">
                    <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray" data-id="element-1157">
                      {group.title}
                    </p>
                    <p className="text-xs text-film-gray mt-0.5" data-id="element-1158">
                      {group.description}
                    </p>
                  </div>
                  <p className="font-mono text-[10px] text-film-gray" data-id="element-1159">
                    {groupFrames.length} frames
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-id="element-1160">
                  {groupFrames.map(f => <div key={f.id} className="flex flex-col" data-id="element-1161">
                      <FrameThumb frame={f} data-id="element-1162" />
                      <p className="font-mono text-[9px] uppercase tracking-wider text-film-gray mt-2 truncate" data-id="element-1163">
                        {f.id}
                      </p>
                      <p className="font-display font-bold text-xs text-film-black leading-tight mt-0.5" data-id="element-1164">
                        {f.name}
                      </p>
                      <p className="font-mono text-[9px] text-film-gray mt-0.5" data-id="element-1165">
                        {f.dims} · {f.ratio}
                      </p>
                    </div>)}
                </div>
              </div>;
        })}
        </div>
      </section>

      {/* Export instructions */}
      <section data-id="element-1166">
        <div className="mb-6" data-id="element-1167">
          <p className="font-mono text-[10px] uppercase tracking-wider text-film-gray mb-1" data-id="element-1168">
            § INSTRUCTIONS
          </p>
          <h2 className="font-display font-bold text-2xl text-film-black" data-id="element-1169">
            How to open in each tool
          </h2>
          <p className="text-sm text-film-gray mt-1 max-w-xl" data-id="element-1170">
            Step-by-step for Figma and Affinity Publisher. Both keep the
            CAT_/FMT_ frame names intact.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1171">
          {/* Figma */}
          <div className="bg-white border border-film-border shadow-card" data-id="element-1172">
            <div className="bg-film-black text-white px-5 py-3 flex items-center justify-between" data-id="element-1173">
              <div className="flex items-center gap-2" data-id="element-1174">
                <FileBox className="w-4 h-4" data-id="element-1175" />
                <p className="font-display font-bold" data-id="element-1176">Figma</p>
              </div>
              <p className="font-mono text-[9px] text-white/50 uppercase tracking-wider" data-id="element-1177">
                tokens + svg · web + desktop
              </p>
            </div>
            <div className="p-5 space-y-3" data-id="element-1178">
              {['Download the Figma bundle (.zip) above and unzip it.', 'Activate Futura PT, Helvetica Neue, JetBrains Mono in your account.', 'Install the Tokens Studio for Figma plugin (free).', 'Plugins → Tokens Studio → Import → drop in tokens.json.', 'Drag colors.svg + logo-set.svg onto a 00_README page.', 'Drag each /frames/*.svg onto its source page (02_FEED, 03_STORIES, etc).', 'Replace placeholders with real artwork — keep CAT_/FMT_ names.'].map((step, i) => <div key={i} className="flex items-baseline gap-3" data-id="element-1179">
                  <span className="font-mono text-[10px] text-film-red shrink-0" data-id="element-1180">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-sm text-film-black leading-snug" data-id="element-1181">{step}</p>
                </div>)}
              <p className="font-mono text-[9px] text-film-gray pt-2 border-t border-film-border mt-3 leading-snug" data-id="element-1182">
                Want a 1:1 capture of the rendered designs? Publish this project
                to a URL, then use the html.to.design Figma plugin on it.
              </p>
            </div>
          </div>

          {/* Affinity */}
          <div className="bg-white border border-film-border shadow-card" data-id="element-1183">
            <div className="text-white px-5 py-3 flex items-center justify-between" style={{
            backgroundColor: C_BLUE
          }} data-id="element-1184">
              <div className="flex items-center gap-2" data-id="element-1185">
                <Layers className="w-4 h-4" data-id="element-1186" />
                <p className="font-display font-bold" data-id="element-1187">Affinity Publisher</p>
              </div>
              <p className="font-mono text-[9px] text-white/70 uppercase tracking-wider" data-id="element-1188">
                cmyk · v2+
              </p>
            </div>
            <div className="p-5 space-y-3" data-id="element-1189">
              {['Download the Affinity bundle (.zip) above and unzip it.', 'Install the 3 fonts system-wide so Publisher picks them up.', 'New document → CMYK/8 + FOGRA39 or US Web Coated v2.', 'File → Place → colors.svg to build the swatch palette.', 'Add CMYK values manually from colors-cmyk.txt.', 'Place each /frames/*.svg onto its corresponding spread.', 'Export PDF/X-1a for press, PDF/X-4 for digital.'].map((step, i) => <div key={i} className="flex items-baseline gap-3" data-id="element-1190">
                  <span className="font-mono text-[10px] shrink-0" style={{
                color: C_BLUE
              }} data-id="element-1191">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-sm text-film-black leading-snug" data-id="element-1192">{step}</p>
                </div>)}
            </div>
          </div>
        </div>

        {/* Naming reminder */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-1193">
          <CopyBlock label="Frame naming" value="CAT_{category} / FMT_{format} · {variant}" data-id="element-1194" />
          <CopyBlock label="Slide naming" value="FMT_CAROUSEL_{n}/{total}" data-id="element-1195" />
          <CopyBlock label="Print naming" value="FMT_PRINT / {piece} · {face}" data-id="element-1196" />
        </div>
      </section>

      {/* Footer */}
      <section className="bg-film-black text-white p-8 shadow-card" data-id="element-1197">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center" data-id="element-1198">
          <div data-id="element-1199">
            <p className="font-mono text-[10px] uppercase tracking-wider text-white/50 mb-2" data-id="element-1200">
              Version log
            </p>
            <h3 className="font-display font-bold text-2xl leading-tight mb-2" data-id="element-1201">
              Film Notes · Template Library v1.0
            </h3>
            <p className="text-sm text-white/60 leading-snug" data-id="element-1202">
              May 2026 · Internal. Update this page whenever a new frame ships
              so the master count stays honest.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs" data-id="element-1203">
            {[{
            label: 'Frames',
            value: totalFrames
          }, {
            label: 'Pages',
            value: 6
          }, {
            label: 'Categories',
            value: totalCats
          }, {
            label: 'Ratios',
            value: totalFormats
          }].map(s => <div key={s.label} className="border border-white/15 px-4 py-3" data-id="element-1204">
                <p className="font-mono text-[9px] uppercase tracking-wider text-white/50 mb-1" data-id="element-1205">
                  {s.label}
                </p>
                <p className="font-display font-bold text-xl" data-id="element-1206">{s.value}</p>
              </div>)}
          </div>
        </div>
      </section>
    </div>;
}